import { expandCountrySelectionToDisplayNames } from '@/constants/country-groups';

// Client-side location component parsers.
//
// Crustdata's location autocomplete (field='location') returns strings
// like "San Francisco, California, USA" or "Texas, USA" without any
// structured indication of which component is the city, state, or
// country. The backend has Python helpers for this (cleanse-api-v2/
// cleanse_api/api/people_search_api.py:372-389) — these mirror them
// in TypeScript so the frontend can scope autocomplete suggestions
// (only city results in City field, only state results in State, etc.)
// without paying for a roundtrip.
//
// Rules (matching the Python implementation):
//  - 2 comma-separated parts → state, country  (parts[0] is state,
//    parts[1] is country)
//  - 3+ parts → city, state(s), country  (parts[0] is city, last
//    is country, middle parts joined are state)

export function splitLocationParts(value: string): string[] {
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * "San Francisco, California, USA" → "San Francisco"
 * "Texas, USA" → "" (no city component when only 2 parts)
 */
export function extractCity(value: string): string {
  const parts = splitLocationParts(value);
  if (parts.length >= 3) return parts[0];
  return '';
}

/**
 * "San Francisco, California, USA" → "California"
 * "Texas, USA" → "Texas"
 * "USA" → ""
 *
 * Mirrors _extract_region_component from the Python backend — when
 * there are more than three parts (e.g. "Brooklyn, Kings County, New
 * York, USA") the middle parts are joined.
 */
export function extractRegion(value: string): string {
  const parts = splitLocationParts(value);
  if (parts.length === 2) return parts[0];
  if (parts.length >= 3) return parts.slice(1, -1).join(', ');
  return '';
}

/**
 * "San Francisco, California, USA" → "USA"
 * "Texas, USA" → "USA"
 * "USA" → "USA"
 */
export function extractCountry(value: string): string {
  const parts = splitLocationParts(value);
  if (parts.length === 1) return parts[0];
  if (parts.length >= 2) return parts[parts.length - 1];
  return '';
}

export type LocationScope = 'country' | 'state' | 'city';

/**
 * Filter raw Crustdata location suggestions down to the ones that match
 * the requested scope. Used by IncludeExcludeAutocomplete when given a
 * scope so typing "Texas" in the State field doesn't return city-level
 * matches.
 *
 * Returns objects with both the display label (what the user sees in
 * the dropdown) and the value (what gets committed to the filter). For
 * country scope we collapse to just the country name; for state we
 * show "State, Country"; for city we show the full triple.
 */
export interface LocationScopeContext {
  /** Countries already picked in the sibling country control, in any form
   *  the state can carry (iso3, full name, display name, group token). */
  countries?: string[];
  /** States already picked, as committed ("New Hampshire, United States"). */
  states?: string[];
}

export function filterLocationSuggestions(
  raw: string[],
  scope: LocationScope,
  query: string,
  context: LocationScopeContext = {}
): Array<{ label: string; value: string }> {
  const q = query.trim().toLowerCase();
  const seen = new Set<string>();
  const out: Array<{ label: string; value: string }> = [];

  // Narrow the dropdown to what the sibling controls already committed to.
  // Without this, typing "manc" under a United States filter offers
  // Manchester UK above Manchester NH, and picking it silently searches the
  // wrong country. Countries are normalized to the DISPLAY spelling because
  // that is what Crustdata's suggestion strings carry ("United States", not
  // "United States of America").
  const scopeCountries = new Set(
    (context.countries?.length
      ? expandCountrySelectionToDisplayNames(context.countries)
      : []
    ).map((c) => c.toLowerCase())
  );
  // A committed state is "State, Country"; compare on the state component.
  const scopeStates = new Set(
    (context.states ?? [])
      .map((s) => extractRegion(s) || s)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
  const hasScope = scopeCountries.size > 0 || scopeStates.size > 0;

  for (const suggestion of raw) {
    if (typeof suggestion !== 'string') continue;
    const parts = splitLocationParts(suggestion);
    if (parts.length === 0) continue;

    // Scope check runs BEFORE the per-scope shaping so it applies uniformly
    // to the city and state lists.
    if (scopeCountries.size > 0 && parts.length > 1) {
      // Only reject a suggestion that NAMES a country and names the wrong
      // one. A single-component value ("Bay Area") names no country, so it
      // is country-less rather than out-of-country — and the query side now
      // ANDs the picked country against exactly those values, so keeping it
      // pickable is safe.
      const country = extractCountry(suggestion).toLowerCase();
      if (!country || !scopeCountries.has(country)) continue;
    }
    if (scopeStates.size > 0 && scope === 'city') {
      const state = extractRegion(suggestion).toLowerCase();
      // 4-part values join their middle components, so also accept the
      // second-to-last part on its own ("Kings County, New York" -> "New York").
      const bare =
        parts.length >= 3 ? parts[parts.length - 2].trim().toLowerCase() : '';
      if (!scopeStates.has(state) && !scopeStates.has(bare)) continue;
    }

    let label = '';
    let value = '';

    if (scope === 'country') {
      const country = extractCountry(suggestion);
      if (!country) continue;
      if (q && !country.toLowerCase().includes(q)) continue;
      label = country;
      value = country;
    } else if (scope === 'state') {
      // Require at least 2 components (state, country) — pure country
      // suggestions are filtered out.
      if (parts.length < 2) continue;
      const region = extractRegion(suggestion);
      const country = extractCountry(suggestion);
      if (!region) continue;
      if (q && !region.toLowerCase().includes(q)) continue;
      label = country ? `${region}, ${country}` : region;
      value = label;
    } else {
      // city: require 3+ components so "Texas, USA" doesn't surface
      if (parts.length < 3) continue;
      const city = extractCity(suggestion);
      if (!city) continue;
      if (q && !city.toLowerCase().includes(q)) continue;
      label = suggestion;
      value = suggestion;
    }

    if (seen.has(label)) continue;
    seen.add(label);
    out.push({ label, value });
  }

  // Fallback — if scope filtering stripped everything, show the raw
  // suggestions (deduped) so the user sees at least something. The
  // city scope is strict (3+ comma parts) and Crustdata sometimes
  // returns mostly metro-area aggregates like "Bay Area" that have
  // only 1-2 parts. Better to show those than an empty dropdown.
  // Only fall back when NOTHING was asked of us. With an active scope the
  // fallback was the leak itself: it dumped the unfiltered list, which is how
  // a US-scoped search surfaced UK and Australian cities. An empty dropdown
  // under an active filter is honest; a wrong one is not.
  if (out.length === 0 && raw.length > 0) {
    for (const suggestion of raw) {
      if (typeof suggestion !== 'string') continue;
      if (seen.has(suggestion)) continue;
      // Under an active scope the fallback is filtered, not blind. Dumping
      // the raw list was the leak itself — it is how a US-scoped search
      // surfaced UK cities. Country-less aggregates still pass (see above);
      // a suggestion naming the wrong country never does.
      if (hasScope && splitLocationParts(suggestion).length > 1) {
        const country = extractCountry(suggestion).toLowerCase();
        if (scopeCountries.size > 0 && !scopeCountries.has(country)) continue;
      }
      seen.add(suggestion);
      out.push({ label: suggestion, value: suggestion });
    }
  }

  return out;
}

export interface DecomposedLocations {
  cities: string[];
  states: string[];
  countries: string[];
  /** Values with too few components to decompose (bare "Manchester", metro
   *  aggregates like "Bay Area"). Callers keep these on the substring path. */
  unresolved: string[];
}

/**
 * Split committed location values into their structured components so a
 * search can filter Crustdata's LABELLED fields (``location_details.city`` /
 * ``.state`` / ``.country``) instead of substring-matching the free-text
 * ``region`` blob.
 *
 * This is what makes country actually constrain a city search. The blob
 * approach could only test "does this address contain 'Manchester'", which
 * matches Manchester UK just as happily as Manchester NH — so the builders
 * had to DROP the country filter to avoid two substring tests fighting over
 * one string. Against labelled fields, city/state/country are independent
 * columns that AND correctly, and multiple cities are a native ``in`` list
 * (OR) rather than a hand-built group.
 *
 * Note this deliberately does NOT reuse ``extractRegion``: for 4+ part values
 * ("Brooklyn, Kings County, New York, United States") that helper joins the
 * middle parts into "Kings County, New York", which matches no state row.
 * The state is always the SECOND-TO-LAST component; intermediate county
 * components are dropped.
 */
export function decomposeLocationValues(values: string[]): DecomposedLocations {
  const cities = new Set<string>();
  const states = new Set<string>();
  const countries = new Set<string>();
  const unresolved: string[] = [];

  for (const raw of values) {
    const parts = splitLocationParts(raw);
    if (parts.length >= 3) {
      cities.add(parts[0]);
      states.add(parts[parts.length - 2]);
      countries.add(parts[parts.length - 1]);
    } else if (parts.length === 2) {
      states.add(parts[0]);
      countries.add(parts[1]);
    } else if (parts.length === 1) {
      unresolved.push(parts[0]);
    }
  }

  return {
    cities: [...cities],
    states: [...states],
    countries: [...countries],
    unresolved,
  };
}
